import React, { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { fetchData, postData, patchData } from "../utils/api";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import PayloadEditorModal from "../components/attendee-value/PayloadEditorModal";
import EstimationQueuePanel from "../components/attendee-value/EstimationQueuePanel";

const AdminAttendeeValueOverridesPage = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [backfillingCompanyIds, setBackfillingCompanyIds] = useState(new Set());
  const [backfillPage, setBackfillPage] = useState(1);
  const [backfillView, setBackfillView] = useState("not_run");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [payloadEditorOpen, setPayloadEditorOpen] = useState(false);

  const handleQueueComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["backfillCompanies"] });
  }, [queryClient]);

  const handleQueueProgress = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["backfillCompanies"] });
  }, [queryClient]);
  const [expandedPromptCompanyId, setExpandedPromptCompanyId] = useState(null);
  const [inlinePromptText, setInlinePromptText] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setBackfillPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["adminOverrides", currentPage],
    queryFn: () =>
      fetchData(`api/admin/attendee_value_overrides?page=${currentPage}`),
  });

  const { data: backfillData, isLoading: backfillLoading } = useQuery({
    queryKey: ["backfillCompanies", backfillPage, backfillView, debouncedSearch],
    queryFn: () =>
      fetchData(
        `api/admin/companies/backfill_index?page=${backfillPage}&view=${backfillView}&search=${encodeURIComponent(debouncedSearch)}`
      ),
    placeholderData: keepPreviousData,
  });

  const backfillMutation = useMutation({
    mutationFn: (companyId) =>
      postData(`api/admin/companies/${companyId}/attendee_value_estimates/backfill`),
    onSuccess: (res, companyId) => {
      if (res.queued_count === 0) {
        const msg = getZeroQueueMessage(res);
        toast.warning(msg);
      } else {
        toast.success(`Backfill queued for ${res.queued_count} events`);
        window.__estimationQueueRefresh?.();
      }
      setBackfillingCompanyIds((prev) => {
        const next = new Set(prev);
        next.delete(companyId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["backfillCompanies"] });
    },
    onError: (err, companyId) => {
      const message = err?.response?.data?.error || "Failed to trigger backfill";
      toast.error(message);
      setBackfillingCompanyIds((prev) => {
        const next = new Set(prev);
        next.delete(companyId);
        return next;
      });
    },
  });

  const promptMutation = useMutation({
    mutationFn: ({ companyId, promptAddendum }) =>
      patchData(`api/admin/companies/${companyId}/attendee_value_prompt`, {
        prompt_addendum: promptAddendum,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["backfillCompanies"] });
      queryClient.invalidateQueries({ queryKey: ["adminOverrides"] });
      setExpandedPromptCompanyId(null);
      toast.success("Prompt updated");
    },
    onError: () => {
      toast.error("Failed to update prompt");
    },
  });

  const getZeroQueueMessage = (res) => {
    const d = res.diagnostics || {};
    switch (res.reason) {
      case "no_events":
        return "This company has no events.";
      case "no_promotions":
        return "This company has no events with promotions.";
      case "no_eligible_events":
        return `No events with promotions completed since ${d.cutoff_date || "Jan 01, 2026"}.`;
      case "all_already_completed":
        return `All ${d.events_after_cutoff || 0} eligible events already estimated.`;
      default:
        return "No eligible events found for backfill.";
    }
  };

  const handleSaveInlinePrompt = (companyId) => {
    promptMutation.mutate({ companyId, promptAddendum: inlinePromptText });
  };

  const handleBackfill = (companyId) => {
    setBackfillingCompanyIds((prev) => new Set([...prev, companyId]));
    backfillMutation.mutate(companyId);
  };

  const togglePromptExpand = (company) => {
    if (expandedPromptCompanyId === company.id) {
      setExpandedPromptCompanyId(null);
    } else {
      setExpandedPromptCompanyId(company.id);
      setInlinePromptText("");
    }
  };

  if (error)
    return (
      <div className="alert alert-danger m-4">
        Failed to load overrides. Admin access required.
      </div>
    );
  if (isLoading || !data) return <Loader />;

  return (
    <div className="container-fluid py-4 performance">
      <h1 className="mb-4 font-size-22">Attendee Value Overrides</h1>

      <EstimationQueuePanel onComplete={handleQueueComplete} onProgress={handleQueueProgress} />

      {/* Company Backfill Section */}
      <div className="card event_card custom-shadow mb-4">
          <div className="event_card__heading">
          <h4>Run Backfill by Company</h4>
          <button
            className="btn btn-sm border border-primary-blue primary-blue d-inline-flex align-items-center"
            onClick={() => setPayloadEditorOpen(true)}
          >
            <i className="fa fa-cog primary-purple" style={{ marginRight: "0.5rem" }}></i>
            Configure AI Payloads
          </button>
        </div>
        <div className="card-body">
          <p className="mb-3 events-th" style={{ fontSize: "14px" }}>
            Trigger attendee value estimation for all eligible events of a specific company.
            Only companies with the feature gate enabled are shown.
          </p>
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3 flex-wrap">
            <input
              type="text"
              className="form-control rounded-pill"
              placeholder="Search companies..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ maxWidth: "300px", border: "1px solid #D6D6D6", padding: "8px 14px" }}
            />
            <div className="tab-buttons bg-light custom-shadow d-flex px-4 events-tabs admin-backfill-tabs justify-content-between">
              {["not_run", "completed"].map((tab) => (
                <button
                  key={tab}
                  className={`btn rounded-pill event-tabs-btn admin-backfill-tab-btn ${
                    backfillView === tab
                      ? "primary-purple text-white font-weight-bold"
                      : "bg-transparent text-dark font-weight-normal"
                  }`}
                  style={{ border: "none", fontWeight: "500" }}
                  onClick={() => {
                    setBackfillView(tab);
                    setBackfillPage(1);
                  }}
                >
                  {tab === "not_run" ? "Not Yet Run" : "Completed"}
                </button>
              ))}
            </div>
          </div>
          {backfillLoading && !backfillData ? (
            <Loader />
          ) : !backfillData?.companies?.length ? (
            <div className="text-center py-5">
              <i className="fa fa-check-circle text-muted mb-2" style={{ fontSize: "2rem", display: "block" }}></i>
              <p className="text-muted mb-0" style={{ fontSize: "15px" }}>
                {debouncedSearch
                  ? "No matching companies found."
                  : backfillView === "not_run"
                  ? "All companies with the feature gate have been backfilled."
                  : "No backfills yet. Switch to Not Yet Run to start."}
              </p>
            </div>
          ) : (
            <div className="table-responsive table-wrapper">
              <table className="table bg-white table-container mb-0 backfill-companies-table">
                <thead className="table-header-bold">
                  <tr>
                    <th className="border-0 events-th backfill-company-col">Companies With Feature Gate Enabled</th>
                    {backfillView === "not_run" ? (
                      <>
                        <th className="border-0 events-th">Eligible Events</th>
                        <th className="border-0 events-th" style={{ width: "80px" }}>Prompt</th>
                        <th className="border-0 events-th" style={{ width: "150px" }}>Action</th>
                      </>
                    ) : (
                      <>
                        <th className="border-0 events-th">Progress</th>
                        <th className="border-0 events-th">Last Run</th>
                        <th className="border-0 events-th" style={{ width: "80px" }}>Prompt</th>
                        <th className="border-0 events-th" style={{ width: "150px" }}>Action</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="table-data">
                  {backfillData.companies.map((company) => {
                    const isBackfilling = backfillingCompanyIds.has(company.id);
                    const total = company.total_eligible_events || 0;
                    const completed = company.completed_estimates || 0;
                    const pending = company.pending_estimates || 0;
                    const failed = company.failed_estimates || 0;

                    return (
                      <React.Fragment key={company.id}>
                        <tr>
                          <td className="border-top events-tr backfill-company-col">{company.name}</td>
                          {backfillView === "not_run" ? (
                            <>
                              <td className="border-top events-tr">
                                <span className={total === 0 ? "text-muted" : ""}>
                                  {total} event{total !== 1 ? "s" : ""}
                                </span>
                              </td>
                              <td className="border-top events-tr">
                                <button
                                  className="btn btn-sm btn-link p-0"
                                  onClick={() => togglePromptExpand(company)}
                                  title={company.has_custom_prompt ? "Custom prompt set" : "No custom prompt"}
                                >
                                  <i className={`fa fa-comment-dots ${company.has_custom_prompt ? "text-success" : "text-muted"}`}></i>
                                </button>
                              </td>
                              <td className="border-top">
                                <button
                                  className="btn btn-sm border border-primary-blue primary-blue d-inline-flex align-items-center"
                                  onClick={() => handleBackfill(company.id)}
                                  disabled={isBackfilling}
                                >
                                  {isBackfilling ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm" style={{ marginRight: "0.5rem" }}></span>
                                      Running...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fa fa-sync primary-purple" style={{ marginRight: "0.5rem" }}></i> Backfill
                                    </>
                                  )}
                                </button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="border-top events-tr">
                                <span>
                                  {completed}/{total}
                                  {completed === total && total > 0 && (
                                    <i className="fa fa-check-circle ms-1 text-green" title="All completed"></i>
                                  )}
                                  {pending > 0 && (
                                    <i className="fa fa-spinner fa-spin text-warning ms-1" title={`${pending} pending`}></i>
                                  )}
                                  {failed > 0 && (
                                    <i className="fa fa-exclamation-triangle text-danger ms-1" title={`${failed} failed`}></i>
                                  )}
                                </span>
                              </td>
                              <td className="border-top events-tr text-nowrap" style={{ fontSize: "14px" }}>
                                {company.last_run_at || "—"}
                              </td>
                              <td className="border-top events-tr">
                                <button
                                  className="btn btn-sm btn-link p-0"
                                  onClick={() => togglePromptExpand(company)}
                                  title={company.has_custom_prompt ? "Custom prompt set" : "No custom prompt"}
                                >
                                  <i className={`fa fa-comment-dots ${company.has_custom_prompt ? "text-success" : "text-muted"}`}></i>
                                </button>
                              </td>
                              <td className="border-top">
                                <button
                                  className="btn btn-sm border border-primary-blue primary-blue d-inline-flex align-items-center"
                                  onClick={() => handleBackfill(company.id)}
                                  disabled={isBackfilling}
                                >
                                  {isBackfilling ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm" style={{ marginRight: "0.5rem" }}></span>
                                      Running...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fa fa-sync primary-purple" style={{ marginRight: "0.5rem" }}></i> Re-run
                                    </>
                                  )}
                                </button>
                              </td>
                            </>
                          )}
                        </tr>
                        {expandedPromptCompanyId === company.id && (
                          <tr>
                            <td colSpan={backfillView === "not_run" ? 4 : 5} className="bg-light">
                              <div className="p-2">
                                <label className="form-label mb-1 events-th" style={{ fontSize: "14px" }}>
                                  Per-company prompt addendum for {company.name}
                                </label>
                                <textarea
                                  className="form-control form-control-sm mb-2"
                                  rows="3"
                                  value={inlinePromptText}
                                  onChange={(e) => setInlinePromptText(e.target.value)}
                                  placeholder="Add extra context about this company's events (e.g., 'This company runs food festivals with avg $40 food spend per person')..."
                                  style={{ fontSize: "15px" }}
                                />
                                <div className="d-flex gap-2">
                                  <button
                                    className="btn btn-sm primary-purple text-white"
                                    style={{ border: "none" }}
                                    onClick={() => handleSaveInlinePrompt(company.id)}
                                    disabled={promptMutation.isPending}
                                  >
                                    <i className="fa fa-check me-1"></i> Save
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setExpandedPromptCompanyId(null)}
                                  >
                                    Cancel
                                  </button>
                                  {company.has_custom_prompt && (
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => {
                                        setInlinePromptText("");
                                        promptMutation.mutate({ companyId: company.id, promptAddendum: "" });
                                      }}
                                      disabled={promptMutation.isPending}
                                    >
                                      Clear prompt
                                    </button>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {backfillData && backfillData.total_pages > 1 && (
            <div className="d-flex justify-content-end align-items-center mt-4 mb-3">
              <div className="btn-group" role="group">
                <button
                  className="btn pagination-btn px-4 rounded-pill"
                  onClick={() => setBackfillPage(backfillPage - 1)}
                  disabled={backfillPage === 1}
                >
                  Previous
                </button>
                <button className="pagination-btn px-4 custom-pagination-btn rounded-pill mx-2" disabled>
                  Page {backfillData.current_page} of {backfillData.total_pages}
                </button>
                <button
                  className="btn pagination-btn px-4 rounded-pill"
                  onClick={() => setBackfillPage(backfillPage + 1)}
                  disabled={backfillPage === backfillData.total_pages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Overrides Table */}
      {data.overrides.length === 0 ? (
        <div className="card event_card custom-shadow">
          <div className="event_card__heading">
            <h4>Override History</h4>
          </div>
          <div className="card-body text-center py-5">
            <i className="fa fa-clipboard-list text-muted mb-2" style={{ fontSize: "2rem", display: "block" }}></i>
            <p className="text-muted mb-0" style={{ fontSize: "15px" }}>No overrides have been submitted yet.</p>
          </div>
        </div>
      ) : (
        <div className="card event_card custom-shadow">
          <div className="event_card__heading">
            <h4>Override History</h4>
          </div>
          <div className="table-responsive table-wrapper">
            <table className="table bg-white table-container mb-0">
              <thead className="table-header-bold">
                <tr>
                  <th className="border-0 events-th override-history-event-header">Event</th>
                  <th className="border-0 events-th">Company</th>
                  <th className="border-0 events-th">User</th>
                  <th className="border-0 events-th">Override Value</th>
                  <th className="border-0 events-th">Reason</th>
                  <th className="border-0 events-th">Date</th>
                </tr>
              </thead>
              <tbody className="table-data">
                {data.overrides.map((o) => (
                  <tr key={o.id}>
                    <td className="border-top events-tr override-history-event-cell">
                      <a
                        href={`/events/${o.event_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none event-title-text"
                      >
                        {o.event_name}
                      </a>
                    </td>
                    <td className="border-top events-tr">{o.company_name}</td>
                    <td className="border-top events-tr">{o.user_name}</td>
                    <td className="border-top events-tr text-green">${Number(o.override_value).toFixed(2)}</td>
                    <td className="border-top events-tr" style={{ maxWidth: "200px" }}>{o.reason}</td>
                    <td className="border-top events-tr text-nowrap">{o.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.total_pages > 1 && (
            <div className="card-footer d-flex justify-content-end">
              <div className="btn-group" role="group">
                <button
                  className="btn pagination-btn px-4 rounded-pill"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <button className="pagination-btn px-4 custom-pagination-btn rounded-pill mx-2" disabled>
                  Page {currentPage} of {data.total_pages}
                </button>
                <button
                  className="btn pagination-btn px-4 rounded-pill"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === data.total_pages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <PayloadEditorModal
        show={payloadEditorOpen}
        onClose={() => setPayloadEditorOpen(false)}
      />
    </div>
  );
};

export default AdminAttendeeValueOverridesPage;
